import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { forwardRef } from 'react';
import { cx } from '../../../internal/cx.js';
import { formatNumber } from '../../../internal/format-number.js';
import { formatMoney } from '../../billing/money/index.js';
import { VisuallyHidden } from '../../layout/visually-hidden/index.js';
const DEFAULT_LABELS = {
    up: 'up',
    down: 'down',
    flat: 'No change',
    better: 'better',
    worse: 'worse',
    noData: 'No earlier data',
};
const MINUS = '−';
function resolveValue(value, current, previous, format) {
    if (value !== undefined)
        return value !== null && Number.isFinite(value) ? value : null;
    if (current == null || previous == null)
        return null;
    if (format === 'percent')
        return previous === 0 ? null : (current - previous) / Math.abs(previous);
    return current - previous;
}
/**
 * Works out the direction, tone, visible text and spoken label of a change.
 * Use it for chart summaries and `aria-label`s so they match DeltaIndicator.
 */
export function formatDelta(options) {
    const { value: rawValue, current, previous, format = 'percent', currency, unit, locale, formatOptions, formatValue, goodDirection = 'up', flatThreshold = 0, comparison, } = options;
    const labels = { ...DEFAULT_LABELS, ...options.labels };
    const value = resolveValue(rawValue, current, previous, format);
    if (value === null) {
        return {
            value: null,
            direction: null,
            tone: 'neutral',
            text: labels.noData,
            magnitude: '',
            label: labels.noData,
        };
    }
    const write = (n) => {
        if (formatValue)
            return formatValue(n);
        if (format === 'currency') {
            return formatMoney(n, currency ?? '', {
                ...(locale !== undefined && { locale }),
                ...(formatOptions?.minimumFractionDigits !== undefined && {
                    minimumFractionDigits: formatOptions.minimumFractionDigits,
                }),
                ...(formatOptions?.maximumFractionDigits !== undefined && {
                    maximumFractionDigits: formatOptions.maximumFractionDigits,
                }),
            });
        }
        const text = formatNumber(n, {
            kind: format,
            ...(locale !== undefined && { locale }),
            ...(formatOptions !== undefined && { options: formatOptions }),
        });
        return unit && format === 'number' ? `${text}\u00a0${unit}` : text;
    };
    const size = Math.abs(value);
    // A change that rounds to zero when written ("0%") is flat, not "+0%".
    const flat = size <= flatThreshold || write(size) === write(0);
    const tail = comparison ? ` ${comparison}` : '';
    if (flat) {
        return {
            value,
            direction: 'flat',
            tone: 'neutral',
            text: labels.flat,
            magnitude: '',
            label: `${labels.flat}${tail}`,
        };
    }
    const direction = value > 0 ? 'up' : 'down';
    const tone = goodDirection === 'neither' ? 'neutral' : direction === goodDirection ? 'positive' : 'negative';
    const magnitude = write(size);
    const quality = tone === 'positive' ? labels.better : tone === 'negative' ? labels.worse : '';
    return {
        value,
        direction,
        tone,
        text: `${direction === 'up' ? '+' : MINUS}${magnitude}`,
        magnitude,
        label: `${labels[direction]} ${magnitude}${tail}${quality ? `, ${quality}` : ''}`,
    };
}
const glyph = {
    width: '1em',
    height: '1em',
    viewBox: '0 0 16 16',
    fill: 'none',
    stroke: 'currentColor',
    strokeWidth: 2,
    strokeLinecap: 'round',
    strokeLinejoin: 'round',
    'aria-hidden': true,
    focusable: false,
};
function DirectionGlyph({ direction, ...props }) {
    return (_jsx("svg", { ...glyph, ...props, children: direction === 'up' ? (_jsx("path", { d: "M4.5 11.5l7-7M5.5 4.5h6v6" })) : direction === 'down' ? (_jsx("path", { d: "M4.5 4.5l7 7M11.5 5.5v6h-6" })) : (_jsx("path", { d: "M3.5 8h9" })) }));
}
/**
 * A change between two periods: direction arrow, signed value and an optional
 * comparison. Colour follows `goodDirection`, so a fall in costs is green;
 * the arrow, sign and spoken "up 12% vs last month, better" carry the meaning
 * without colour.
 */
export const DeltaIndicator = forwardRef(function DeltaIndicator({ value, current, previous, format, currency, unit, locale, formatOptions, formatValue, goodDirection, flatThreshold, labels, comparison, appearance = 'plain', size = 'sm', hideIcon = false, className, ...rest }, ref) {
    const delta = formatDelta({
        ...(value !== undefined && { value }),
        ...(current !== undefined && { current }),
        ...(previous !== undefined && { previous }),
        ...(format !== undefined && { format }),
        ...(currency !== undefined && { currency }),
        ...(unit !== undefined && { unit }),
        ...(locale !== undefined && { locale }),
        ...(formatOptions !== undefined && { formatOptions }),
        ...(formatValue !== undefined && { formatValue }),
        ...(goodDirection !== undefined && { goodDirection }),
        ...(flatThreshold !== undefined && { flatThreshold }),
        ...(labels !== undefined && { labels }),
    });
    const common = {
        ref,
        ...rest,
        className: cx('mn-delta-indicator', className),
        'data-tone': delta.tone,
        'data-appearance': appearance,
        'data-size': size,
    };
    if (delta.direction === null) {
        return (_jsx("span", { ...common, "data-empty": "", children: _jsx("span", { className: "mn-delta-indicator__value", children: delta.text }) }));
    }
    const comparisonNode = comparison !== undefined && comparison !== null && comparison !== false ? (_jsxs("span", { className: "mn-delta-indicator__comparison", children: [" ", comparison] })) : null;
    if (delta.direction === 'flat') {
        return (_jsxs("span", { ...common, "data-direction": "flat", children: [_jsxs("span", { className: "mn-delta-indicator__change", children: [hideIcon ? null : (_jsx(DirectionGlyph, { direction: "flat", className: "mn-delta-indicator__icon" })), _jsx("span", { className: "mn-delta-indicator__value", children: delta.text })] }), comparisonNode] }));
    }
    const resolvedLabels = { ...DEFAULT_LABELS, ...labels };
    const quality = delta.tone === 'positive'
        ? resolvedLabels.better
        : delta.tone === 'negative'
            ? resolvedLabels.worse
            : '';
    return (_jsxs("span", { ...common, "data-direction": delta.direction, children: [_jsxs("span", { className: "mn-delta-indicator__change", "aria-hidden": true, children: [hideIcon ? null : (_jsx(DirectionGlyph, { direction: delta.direction, className: "mn-delta-indicator__icon" })), _jsx("span", { className: "mn-delta-indicator__value", children: delta.text })] }), _jsx(VisuallyHidden, { children: `${resolvedLabels[delta.direction]} ${delta.magnitude}` }), comparisonNode, quality ? _jsx(VisuallyHidden, { children: `, ${quality}` }) : null] }));
});
//# sourceMappingURL=delta-indicator.js.map